/**
 * @voltxampere/engine — PULSE voltage source stamps (Sprint 22 B2)
 *
 * Conceptually a generalised independent voltage source whose
 * value `V(t)` is selected by the SPICE PULSE waveform:
 *
 *   t < TD                                → V1
 *   TD ≤ t < TD+TR                        → linear ramp V1 → V2
 *   TD+TR ≤ t < TD+TR+PW                  → V2
 *   TD+TR+PW ≤ t < TD+TR+PW+TF            → linear ramp V2 → V1
 *   TD+TR+PW+TF ≤ t < TD+PER              → V1
 *   wraps with period PER once t ≥ TD + PER
 *
 * The DC stamp evaluates the waveform at `ctx.t` (which is 0 for
 * a pure DC OP and the current step time during transient).
 * Independent voltage sources contribute one augmented MNA row:
 *
 *   Y[pos][k] += 1, Y[neg][k] -= 1, Y[k][pos] += 1, Y[k][neg] -= 1
 *   rhs[k]    += V(t) * sourceScale
 *
 * AC small-signal is identically zero — the AC stamp lives in
 * `stamps/ac/pulse-source.ts` and only wires the augmented row's
 * unity coefficients.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId } from "../terminal-utils.js";
const DEFAULT_PARAMS = {
    V1: 0,
    V2: 1,
    TD: 0,
    TR: 1e-9,
    TF: 1e-9,
    PW: 0,
    PER: 1e-6,
};
function readNumber(raw, fallback, predicate) {
    if (typeof raw !== "number" || !Number.isFinite(raw))
        return fallback;
    return predicate(raw) ? raw : fallback;
}
/** Defensive re-resolution. Mirrors the Zod schema's defaults. */
export function resolvePulseParams(componentId, params) {
    const V1 = readNumber(params.V1, DEFAULT_PARAMS.V1, () => true);
    const V2 = readNumber(params.V2, DEFAULT_PARAMS.V2, () => true);
    const TD = readNumber(params.TD, DEFAULT_PARAMS.TD, (v) => v >= 0);
    const TR = readNumber(params.TR, DEFAULT_PARAMS.TR, (v) => v > 0);
    const TF = readNumber(params.TF, DEFAULT_PARAMS.TF, (v) => v > 0);
    const PW = readNumber(params.PW, DEFAULT_PARAMS.PW, (v) => v >= 0);
    const PER = readNumber(params.PER, DEFAULT_PARAMS.PER, (v) => v > 0);
    if (PER < TR + PW + TF) {
        throw new ContractViolationError(`pulse-source "${componentId}": PER (${String(PER)}) must be ≥ TR + PW + TF (${String(TR + PW + TF)})`);
    }
    return { V1, V2, TD, TR, TF, PW, PER };
}
/**
 * Evaluate the PULSE waveform at simulation time `t`. Pure
 * function — exported so the transient passthrough and tests can
 * reuse the formula without re-resolving parameters.
 */
export function pulseValueAt(t, p) {
    if (t < p.TD)
        return p.V1;
    // Phase relative to the current period start. Both PER and the
    // mod operation are guarded by the resolver (PER > 0).
    const phase = (t - p.TD) % p.PER;
    if (phase < p.TR) {
        // Rising ramp V1 → V2.
        return p.V1 + ((p.V2 - p.V1) * phase) / p.TR;
    }
    if (phase < p.TR + p.PW) {
        return p.V2;
    }
    if (phase < p.TR + p.PW + p.TF) {
        // Falling ramp V2 → V1.
        const local = phase - (p.TR + p.PW);
        return p.V2 + ((p.V1 - p.V2) * local) / p.TF;
    }
    // Trailing V1 plateau until the next period.
    return p.V1;
}
export const pulseSourceDCStamp = (ctx, component) => {
    const params = resolvePulseParams(component.id, component.params);
    const v = pulseValueAt(ctx.t, params);
    const pos = ctx.nodeIndex(getTerminalNetId(component, "pos"));
    const neg = ctx.nodeIndex(getTerminalNetId(component, "neg"));
    const k = ctx.vSourceIndex(component.id);
    ctx.addY(pos, k, 1);
    ctx.addY(neg, k, -1);
    ctx.addY(k, pos, 1);
    ctx.addY(k, neg, -1);
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + v * ctx.sourceScale;
};
/** Independent V source — one augmented row carrying branch current. */
export const pulseSourceDCStampDefinition = defineDCStamp("pulse-source", pulseSourceDCStamp, [
    "vsource",
]);
//# sourceMappingURL=pulse-source.js.map